// What a visitor sees when the site throws while rendering.
//
// React unmounts the whole tree on a render error, and with nothing above the
// routes that means a black page: no message, no logo, no way back. This is
// the public half of the same fix as AdminErrorBoundary -- an empty region is
// an instruction, never a hole.
//
// Built from the tokens directly, in the shape of the 404 page, so the two
// dead ends read as siblings. It catches render errors only; failures inside
// event handlers and promises never reach it.

import React from 'react';
import { Box, Button, CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import psTheme, { backdrop, surfaces } from '../ps/psTheme';
import '../ps/ps-tokens.css';
import '../ps/learn.css';
import pslogo from './pslogo.png';

export class SiteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // The console is the only record: there is no reporting service behind it.
    console.error('The site crashed while rendering:', error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <ThemeProvider theme={psTheme}>
        <CssBaseline />
        <Box
          sx={{
            ...backdrop,
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: '10px',
            fontFamily: 'var(--ps-font)',
          }}
        >
          <Box
            sx={{
              ...surfaces.panel,
              p: '22px',
              maxWidth: 420,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <img src={pslogo} alt="Phil Speiser" style={{ height: 18, marginBottom: 12 }} />
            <span className="ps-value" style={{ fontSize: 22 }}>
              Something broke
            </span>
            <span className="ps-label ps-label--dim">Reloading usually fixes it</span>
            {/* The message is for whoever is debugging, not for a visitor. */}
            {process.env.NODE_ENV !== 'production' && (
              <span
                className="ps-label ps-label--dim"
                style={{ textTransform: 'none', letterSpacing: 0, wordBreak: 'break-word' }}
              >
                {String(error?.message || error)}
              </span>
            )}
            <Box sx={{ display: 'flex', gap: '4px', mt: '10px' }}>
              <Button variant="contained" onClick={() => window.location.reload()}>
                Reload
              </Button>
              <Button href="/">Back to the videos</Button>
            </Box>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }
}

export default SiteErrorBoundary;
